// lazyWithReload — React.lazy + tự reload khi import() chunk thất bại (stale chunk).
//
// ⭐ BẢN DUY NHẤT cho cả CNV Work SaaS, CNV Connect SaaS và kernel. App KHÔNG được giữ bản
// chép riêng: sự cố 13/08 ("reload liên tục, nhiều tenant") được vá ở bản app của Work, nhưng
// CNV Connect tách kho trước đó nên ôm nguyên bộ gác hỏng thêm ba ngày và tái diễn 16/08
// (sếp Phú: "web admin khi mở tiếng anh đang bị reload liên tục"). Bản chép không tự nhận
// bản vá của bản gốc — nên thứ này phải nằm ở kernel, hai app cùng import.
//
// Vì sao: deploy mới rsync --delete xoá chunk-hash cũ. Tab user đang mở (index.html cũ)
// bấm vào drawer/route lazy → đòi chunk-hash cũ đã bị xoá → 404 "Failed to fetch
// dynamically imported module" → Vite reload lung tung / trắng trang. index.html để
// no-cache nên reload lấy index mới + chunk mới → tự khỏi.
//
// Guard: sessionStorage key RIÊNG mỗi chunk (theo `name`) → CHỈ reload 1 lần/chunk.
// Nếu sau reload vẫn fail (lỗi thật, không phải stale) → throw lên ErrorBoundary, KHÔNG
// loop. Key riêng (không dùng chung) để tránh: drawer auto-mở từ URL fail → reload →
// route khác load OK xoá cờ chung → drawer fail lại → reload → loop vô hạn.
//
// name: nếu caller KHÔNG truyền → tự suy key ổn định từ nội dung factory (chuỗi
// factory.toString() chứa đường dẫn/tên chunk → mỗi chunk 1 key). Trước đây default
// 'chunk' làm 176 route name-less xài CHUNG 1 key → hỏng guard khi 2 chunk cùng fail
// (vd page + drawer auto-mở từ URL) → 1 chunk bị chặn reload → văng ErrorBoundary.

import { lazy } from 'react';
import { whenCatalogsLoaded } from '../i18n/runtime.js';

// Trần chờ catalog cụm (xem lazy() bên dưới). Catalog chỉ vài chục KB, thường về trong vài
// chục ms; trần này chỉ để mạng treo không giữ trang ở vòng xoay mãi — quá trần thì vẽ luôn.
const CATALOG_WAIT_MAX_MS = 5000;

// Export vì ErrorBoundary dùng chung cũng phải nhận diện đúng LOẠI lỗi này để đổi thông điệp
// (bản mới vừa cập nhật → nút "Tải lại trang") thay vì đổ oan cho dữ liệu. Một bản regex duy
// nhất: chép thêm bản thứ hai là sớm muộn hai bên lệch nhau khi trình duyệt đổi wording.
export function isChunkLoadError(err) {
  const msg = String(err?.message || err || '');
  // «Unable to preload CSS» = Vite nạp CSS đi kèm chunk hỏng (tệp CSS cũ cũng bị deploy xoá) — cùng
  // loại «bản cũ», trước đây chỉ được bộ bắt vite:preloadError toàn cục của Core reload hộ.
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Unable to preload CSS/i.test(msg);
}

// Bản đang chạy đã CŨ (một chunk nạp NGẦM trả 404 vì deploy mới xoá chunk cũ) ⇒ thôi nạp ngầm
// tiếp: nạp tiếp chỉ đẻ thêm trăm request 404, và tuyệt đối không reload thay người dùng — tab
// đang ngồi yên ở /chat bị tự tải lại là đúng thứ sếp Phú hỏi 09/10/2026. Trang nào người dùng
// THẬT SỰ mở mà chunk đã xoá thì nhánh lazy() bên dưới vẫn reload một lần như cũ.
let _banCu = false;

// djb2 — hash ngắn ổn định để làm sessionStorage key từ chuỗi factory (có thể dài).
function djb2(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

// ⚠️ NGÂN SÁCH RELOAD TOÀN CỤC (thêm 13/08 sau sự cố "reload liên tục" nhiều tenant).
// Cờ per-chunk bên dưới chống được loop VÔ HẠN của MỘT chunk, nhưng KHÔNG chống được
// chuỗi reload: mỗi chunk hỏng được quyền reload 1 lần, mà cửa sổ deploy hỏng thường
// làm hàng chục chunk 404 cùng lúc (12 Drawer Provider + route + prefetch) → boot →
// chunk A reload → boot → B reload → boot → C reload… người dùng thấy nhấp nháy liên tục.
// Ngân sách chung: tối đa MAX_RELOADS lần / RESET_AFTER_MS, hai lần cách nhau MIN_GAP_MS.
// Hết ngân sách thì để lỗi nổi lên ErrorBoundary — thà thấy lỗi còn hơn nhấp nháy vô tận.
const BUDGET_KEY = 'cnv:chunk-reload-budget';
const MIN_GAP_MS = 30_000;
const MAX_RELOADS = 2;
const RESET_AFTER_MS = 10 * 60_000;

function takeGlobalReloadSlot() {
  const now = Date.now();
  let st = null;
  try { st = JSON.parse(sessionStorage.getItem(BUDGET_KEY) || 'null'); } catch { /* storage bị chặn */ }
  if (st && now - st.ts < MIN_GAP_MS) return false;
  const n = st && now - st.ts < RESET_AFTER_MS ? st.n : 0;
  if (n >= MAX_RELOADS) return false;
  try { sessionStorage.setItem(BUDGET_KEY, JSON.stringify({ n: n + 1, ts: now })); } catch { /* bỏ qua */ }
  return true;
}

// Sổ mọi factory route → dùng cho prefetch nền (xem prefetchAllRoutes). Mỗi factory chỉ chạy
// một lần (đánh dấu `loaded`) để không tải lặp.
const _factories = [];

// Số chunk mà NGƯỜI DÙNG đang thực sự chờ (một lazy component vừa được render). Prefetch nền
// phải nhường đường cho nó — xem `pump()` bên dưới.
let _dangCho = 0;

export default function lazyWithReload(factory, name) {
  // name tường minh (drawer) → dùng luôn; nếu không → suy từ factory để mỗi chunk 1 key riêng.
  const key = name || `auto-${djb2(String(factory))}`;
  const KEY = `cnv:chunk-reloaded:${key}`;
  const entry = { factory, loaded: false };
  _factories.push(entry);
  const Comp = lazy(async () => {
    _dangCho += 1;
    try {
      const mod = await factory();
      entry.loaded = true;
      // CỐ Ý KHÔNG xoá cờ ở đây. Xoá cờ mỗi lần import thành công CHÍNH LÀ thứ tạo ra loop:
      // app mount nhiều lazy song song, cái nạp OK xoá cờ cho cái 404 được reload lại, vòng
      // vô tận. Cờ tự hết hiệu lực sau RESET_AFTER_MS nên deploy lần sau vẫn tự khỏi.
      //
      // Chunk vừa eval ⇒ shim cụm catalog của nó (src/i18n/c/<cụm>.js) đã bắt đầu tải bản
      // ngôn ngữ đang dùng. Trả module ngay là React vẽ trang khi catalog chưa về ⇒ bản
      // production lòi MÃ BĂM, và chỗ nào đọc `i18n._(msg)` lúc render giữ nguyên mã băm tới
      // lần vẽ lại sau (sự cố /me 12/10/2026 — chi tiết ở whenCatalogsLoaded, i18n/runtime.js).
      // Tiếng Việt / catalog đã có sẵn ⇒ null, không chờ gì.
      const cho = whenCatalogsLoaded();
      if (cho) {
        let hen;
        await Promise.race([cho, new Promise((r) => { hen = setTimeout(r, CATALOG_WAIT_MAX_MS); })]);
        clearTimeout(hen);
      }
      return mod;
    } catch (err) {
      if (isChunkLoadError(err)) {
        let already = false;
        try { already = sessionStorage.getItem(KEY) === '1'; } catch { /* */ }
        if (!already && takeGlobalReloadSlot()) {
          try { sessionStorage.setItem(KEY, '1'); } catch { /* */ }
          window.location.reload();
          return new Promise(() => {}); // treo để React không render lỗi trong lúc reload
        }
      }
      throw err;
    } finally {
      _dangCho -= 1;
    }
  });
  // Hover-prefetch: tải chunk ngay khi người dùng rê chuột vào menu (thanh bên dùng).
  Comp.preload = () => {
    if (!entry.loaded && !_banCu) {
      entry.loaded = true;
      factory().catch((err) => { entry.loaded = false; if (isChunkLoadError(err)) _banCu = true; });
    }
  };
  return Comp;
}

// Tải trước TẤT CẢ chunk route lúc trình duyệt rảnh → bấm menu không còn spinner "đang tải
// trang". Tải tuần tự, cách quãng, bỏ qua khi mạng ở chế độ tiết kiệm dữ liệu hoặc 2G. Chỉ
// chạy một lần mỗi phiên; chunk đã tải thì bỏ qua.
let _prefetchStarted = false;
export function prefetchAllRoutes({ gapMs = 180, startDelayMs = 2500 } = {}) {
  if (_prefetchStarted || typeof window === 'undefined') return;
  _prefetchStarted = true;
  const conn = navigator.connection;
  if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;
  const idle = window.requestIdleCallback || ((cb) => setTimeout(() => cb(), 1));
  let i = 0;
  const pump = () => {
    // ⛔ NHƯỜNG ĐƯỜNG: đang có chunk người dùng chờ (vừa mở drawer, vừa bấm menu) thì hoãn.
    // Đo trên prod 27/08/2026: bấm mở popup việc giữa lúc prefetch chạy thì trình duyệt còn
    // kéo thêm 1.604 KB trong 10 giây — ProjectsMapPage, TemplateBuilderPage,
    // AutomationBuilderPage… chẳng liên quan gì tới việc đang mở. Chunk của drawer xếp hàng
    // sau chỗ đó, và người dùng ngồi nhìn ô trống. Prefetch là để đỡ chờ, không phải để
    // giành băng thông với chính thao tác đang diễn ra.
    if (_banCu) return; // bản đã cũ — xem _banCu
    if (_dangCho > 0) { setTimeout(() => idle(pump), gapMs * 3); return; }
    while (i < _factories.length && _factories[i].loaded) i++;
    if (i >= _factories.length) return;
    const entry = _factories[i++];
    entry.loaded = true;
    Promise.resolve().then(entry.factory).catch((err) => {
      entry.loaded = false;
      if (isChunkLoadError(err)) _banCu = true;
    });
    setTimeout(() => idle(pump), gapMs);
  };
  setTimeout(() => idle(pump), startDelayMs);
}
